import React, { Component, MouseEvent } from 'react';
import styled from 'styled-components';
import moment, { Moment as TMoment } from 'moment';
import { themes } from 'kb-shared';
import ArrowLeft from './SVG/ArrowLeft';
import ArrowRight from './SVG/ArrowRight';

interface ContainerProps {
  renderBorder?: boolean;
}

const Container = styled.div`
  max-width: 330px;
  border: ${(props: ContainerProps) => {
    return props.renderBorder ? 'solid 1px black' : '0px';
  }};
  overflow: 'hidden';
`;

const Navigation = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  height: 46px;
  width: 100%;
`;

const ArrowWrapper = styled.button`
  cursor: pointer;
  padding: 0px 20px 0px 20px;
  border-width: 0px;
  background-color: transparent;
  :focus {
    outline: 0;
  }
`;

const NavText = styled.div``;

const DatesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const RowWrapper = styled.div`
  height: 38px;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const CurrentDate = `
  background-color: ${themes.colors.KBYellow};
  border-radius: 23.5px;
`;

const SelectedDate = `
  background-color: ${themes.colors.KBNight};
  border-radius: 23.5px;
  color: #FFF;
`;

interface DateProps {
  selected?: boolean;
  current?: boolean;
}

const Date = styled.div`
  cursor: pointer;
  width: 25px;
  height: 25px;
  margin: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: black;
  ${(props: DateProps) => {
    return props.selected ? SelectedDate : ``;
  }}
  ${(props: DateProps) => {
    return props.current ? CurrentDate : ``;
  }}
`;

export type CalendarCell = {
  isDate?: boolean;
  isSelected?: boolean;
  isToday?: boolean;
  value: string;
};

export function getCalendarCell(
  value: string,
  isDate?: boolean,
  isSelected?: boolean,
  isToday?: boolean
): CalendarCell {
  return {
    value,
    isDate,
    isSelected,
    isToday
  };
}

export function getCalendarRows(
  currentMonth: TMoment,
  selectedDate: TMoment | null
): Array<Array<CalendarCell>> {
  const daysInMonth = currentMonth.daysInMonth();
  const firstWeekdayOfMonth = currentMonth.startOf('month').day();
  const today = moment();

  const rowsInMonth = Math.ceil((daysInMonth + firstWeekdayOfMonth) / 7);
  const calendarCells = [];
  for (let i = 0; i < rowsInMonth * 7; i++) {
    calendarCells.push(getCalendarCell(''));
  }

  let dayOfMonth = 1;
  for (
    let cellIndex = firstWeekdayOfMonth;
    cellIndex < daysInMonth + firstWeekdayOfMonth;
    cellIndex++
  ) {
    const isToday =
      today.date() === dayOfMonth &&
      today.month() === currentMonth.month() &&
      today.year() === currentMonth.year();

    let isSelected =
      selectedDate &&
      currentMonth.month() === selectedDate.month() &&
      dayOfMonth === selectedDate.date() &&
      currentMonth.year() === selectedDate.year();
    if (!isSelected) isSelected = false;

    calendarCells[cellIndex] = getCalendarCell(dayOfMonth.toString(), true, isSelected, isToday);
    dayOfMonth++;
  }

  const calendarRows = [];
  for (let i = 0; i < rowsInMonth; i++) {
    calendarRows.push(calendarCells.slice(i * 7, (i + 1) * 7));
  }

  return calendarRows;
}

export function getDaysOfWeek() {
  const daysOfWeek = [];

  for (let i = 0; i < 7; i++) {
    const dayOfWeek = moment()
      .isoWeekday(i)
      .format('dd');

    daysOfWeek.push(getCalendarCell(dayOfWeek));
  }

  return daysOfWeek;
}

type Props = {
  selectedDate: TMoment | null;
  currentMonth: TMoment;
  onPrevious: (event: MouseEvent) => void;
  onNext: (event: MouseEvent) => void;
  onSelectDate?: (date: TMoment) => void;
  renderBorder?: boolean;
};

type State = {
  today: TMoment;
};

export default class Calendar extends Component<Props, State> {
  static defaultProps: Props = {
    selectedDate: null,
    currentMonth: moment(),
    onPrevious: () => {},
    onNext: () => {},
    renderBorder: true
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      today: moment()
    };
  }

  selectDate(day: number) {
    const { onSelectDate } = this.props;
    const date = this.props.currentMonth.clone().date(day);
    if (!onSelectDate) return;
    onSelectDate(date);
  }

  render() {
    return (
      <Container renderBorder={this.props.renderBorder}>
        <Navigation>
          <ArrowWrapper onClick={this.props.onPrevious}>
            {' '}
            <ArrowLeft />{' '}
          </ArrowWrapper>
          <NavText>{this.props.currentMonth.format('MMMM')}</NavText>
          <ArrowWrapper onClick={this.props.onNext}>
            <ArrowRight />{' '}
          </ArrowWrapper>
        </Navigation>
        <DatesWrapper>
          {getCalendarRows(moment(this.props.currentMonth), this.props.selectedDate).map(
            (row: CalendarCell[], index: number) => {
              const month = this.props.currentMonth.month();
              return (
                <RowWrapper key={`row-${month}-${index}`}>
                  {row.map((cell: CalendarCell, cellIndex: number) => {
                    return (
                      <Date
                        key={`date-${month}-${cellIndex}`}
                        selected={cell.isSelected}
                        current={cell.isToday}
                        onClick={this.selectDate.bind(this, parseInt(cell.value))}
                      >
                        {cell.value}
                      </Date>
                    );
                  })}
                </RowWrapper>
              );
            }
          )}
        </DatesWrapper>
      </Container>
    );
  }
}
